// @ts-nocheck
import * as React from 'react';
import { CardWrapper, GridWrapper } from './DashbardGridStyledComponents';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  editModeState,
  cardState,
  layoutState,
  namespaceState,
  gridCardState,
} from './DashboardState';
import './DashboardGrid.scss';
import { WidthProvider, Responsive } from 'react-grid-layout';
import DashboardAppBar from './DashboardAppBar';
import { getFromLS, saveToLS } from '../../utils';
import DashboardCard from './DashboardCard';
const ResponsiveReactGridLayout = WidthProvider(Responsive);
const breakpoints = ['lg', 'md', 'sm', 'xs', 'xxs'];
export interface DashboardGridProps {
  onAddItem: () => any;
  onConfigItem: (item: any) => any;
  onRemoveItem: (item: any) => any;
  onDashboardConfig: (download: any) => any;
  onSaveConfig: (download: any) => any;
  title?: string;
  rowHeight?: number;
  defaultBreakpoint?: string;
}

/**
 * This layout demonstrates how to use a grid with a dynamic number of elements.
 */
export const DashboardGrid: React.FC<DashboardGridProps> = React.memo(
  ({
    title = 'Operations',
    defaultBreakpoint = 'lg',
    rowHeight = 100,
    onAddItem,
    onConfigItem,
    onRemoveItem,
    toggleDarkMode,
    onDashboardConfig,
    onSaveConfig,
  }) => {
    const [layouts, setLayouts] = useRecoilState(layoutState);
    const [nCols, setNCols] = React.useState(12);
    const [breakpoint, setBreakpoint] = React.useState(defaultBreakpoint);
    const [transformedCards, setTransformedCards] = React.useState<any[]>([]); // Cards with layout tranformation added
    const [cardsToRender, setCardsToRender] = useRecoilState(gridCardState);
    const editMode = useRecoilValue(editModeState); // if a card is being modified
    const cards = useRecoilValue(cardState); // Current set of cards on the canvas
    const namespace = useRecoilValue(namespaceState);

    React.useEffect(() => {
      const originalLayouts =
        getFromLS(namespace, 'intermix-grid', 'layouts') || {};
      setLayouts(originalLayouts);
    }, [namespace]);

    React.useEffect(() => {
      // If Editing card do not rerender
      if (editMode) {
        return;
      }
      transformCards(cards);
    }, [cards]);

    React.useEffect(() => {
      // Define the Dashboard related constructs for each card to be displayed
      const renderCards: any = transformedCards.map(card => createCardEl(card));
      setCardsToRender(renderCards); // Final set of cards with all display constructs
    }, [transformedCards]);

    /**
     * Add layout information to the cards
     * Also adds layout from saved localstorage if present
     * Basically override with previous state if present
     */
    const transformCards = (cards: any[]) => {
      const newCards: any[] = [];
      let counter = 0;
      cards.map((card: any, i: number) => {
        let x = 0;
        let savedW = -1;
        let savedH = -1;
        let savedX = -1;
        let savedY = -1;
        let savedStatic = false;
        let savedIsResizable = true;
        let savedIsDraggable = true;

        if (
          typeof layouts[breakpoint] !== 'undefined' &&
          typeof layouts[breakpoint][i] !== 'undefined' &&
          typeof layouts[breakpoint][i].cardId !== 'undefined' &&
          layouts[breakpoint][i].cardId === card.cardId
        ) {
          const { w, h, x, y } = layouts[breakpoint][i];
          savedW = w;
          savedH = h;
          savedX = x;
          savedY = y;
          if (typeof layouts[breakpoint][i].static !== 'undefined') {
            savedStatic = layouts[breakpoint][i].static;
            savedIsResizable = savedStatic ? false : true;
            savedIsDraggable = savedStatic ? false : true;
          }
        }
        if (i > 0) {
          let currentW = -1;
          if (
            typeof layouts[breakpoint] !== 'undefined' &&
            typeof layouts[breakpoint][i - 1] !== 'undefined'
          ) {
            const { w } = layouts[breakpoint][i - 1];
            currentW = w;
          }
          const { options } = cards[i - 1];
          const w =
            typeof options !== 'undefined' && typeof options.w !== 'undefined'
              ? options.w
              : 2;
          const incr = currentW > -1 ? currentW : w;
          x = (counter + incr) % nCols;
        }
        newCards.push({
          ...card,
          x,
          savedH,
          savedW,
          savedX,
          savedY,
          static: savedStatic,
          isDraggable: savedIsDraggable,
          isResizable: savedIsResizable,
        });
        counter = x;
      });
      setTransformedCards(newCards);
    };

    const deleteHandler = (cardId: any) => {
      const index = transformedCards.findIndex(item => item.cardId === cardId);
      let updatedLayout = {
        ...layouts,
      };
      breakpoints.map(viewport => {
        if (
          typeof layouts[viewport] !== 'undefined' &&
          typeof layouts[viewport][index] !== 'undefined'
        ) {
          const currentBreakpointLayout = layouts[viewport].filter(
            (layout: any) => layout.cardId !== cardId
          );
          updatedLayout = {
            ...updatedLayout,
            [viewport]: currentBreakpointLayout,
          };
        }
      });
      setLayouts(updatedLayout);
      onRemoveItem(cardId);
    };

    const pinHandler = (cardId: any) => {
      const index = transformedCards.findIndex(item => item.cardId === cardId);
      let updatedLayout = {
        ...layouts,
      };
      breakpoints.map(viewport => {
        if (
          typeof layouts[viewport] !== 'undefined' &&
          typeof layouts[viewport][index] !== 'undefined'
        ) {
          const currentBreakpointLayout = layouts[viewport].map(
            (layout: any) => {
              let newLayout = { ...layout };
              if (layout.cardId === cardId) {
                const value = newLayout.static ? !newLayout.static : true;
                newLayout.static = value;
                newLayout.isDraggable = value ? false : true;
                newLayout.isResizable = value ? false : true;
              }
              return newLayout;
            }
          );
          updatedLayout = {
            ...updatedLayout,
            [viewport]: currentBreakpointLayout,
          };
        }
      });

      const updatedCardsToRender = cardsToRender.map((data: any) => {
        let newEl = { ...data.el };
        if (data.cardId === cardId) {
          const value = newEl.static ? !newEl.static : true;
          newEl.static = value;
          newEl.isDraggable = value ? false : true;
          newEl.isResizable = value ? false : true;
        }
        return { cardId: data.cardId, el: newEl };
      });
      saveToLS(namespace, 'intermix-grid', 'layouts', updatedLayout);
      setLayouts(updatedLayout);
      setCardsToRender(updatedCardsToRender);
    };

    /**
     * Define the Dashboard layout constructs for each card to be displayed
     * @param card
     */
    const createCardEl = (card: any) => {
      const { options } = card;

      const w =
        typeof options !== 'undefined' && typeof options.w !== 'undefined'
          ? options.w
          : 2;

      const h =
        typeof options !== 'undefined' && typeof options.h !== 'undefined'
          ? options.h
          : 2;

      const el = {
        x: card.savedX > -1 ? card.savedX : card.x,
        y: card.savedY > -1 ? card.savedY : 0,
        w: card.savedW > -1 ? card.savedW : w,
        h: card.savedH > -1 ? card.savedH : h,
        static: card.static,
        isDraggable: card.isDraggable,
        isResizable: card.isResizable,
      };

      return { cardId: card.cardId, el };
    };

    const resetLayout = () => {
      saveToLS(namespace, 'intermix-grid', 'layouts', {});
      setLayouts({});
      transformCards(cards);
    };

    // We're using the cols coming back from this to calculate where to add new items.
    const onBreakpointChange = (
      breakpoint: React.SetStateAction<string>,
      cols: React.SetStateAction<number>
    ) => {
      setBreakpoint(breakpoint);
      setNCols(cols);
    };

    const onLayoutChange = (_layout: any, changedLayouts: any) => {
      let finalLayouts = {
        lg: [],
        md: [],
        sm: [],
        xs: [],
        xxs: [],
      } as any;

      Object.entries(changedLayouts).map(data => {
        const key = data[0];
        const value = data[1] as any[];

        const newValue = value.map((item, i) => {
          const cardId = typeof cards[i] !== 'undefined' ? cards[i].cardId : '';
          return { ...item, cardId };
        });
        switch (key) {
          case 'lg':
            finalLayouts.lg = [...newValue];
            break;
          case 'md':
            finalLayouts.md = [...newValue];
            break;
          case 'sm':
            finalLayouts.sm = [...newValue];
            break;
          case 'xs':
            finalLayouts.xs = [...newValue];
            break;
          case 'xxs':
            finalLayouts.xxs = [...newValue];
            break;
          default:
            break;
        }
      });

      saveToLS(namespace, 'intermix-grid', 'layouts', finalLayouts);
      setLayouts(finalLayouts);
    };

    const renderGridCards = () => {
      return cardsToRender.map((data: any) => {
        const card = cards.find(
          (item: { cardId: any }) => item.cardId === data.cardId
        );
        const { el } = data;
        return (
          <CardWrapper id={data.cardId} key={data.cardId} data-grid={el}>
            <DashboardCard
              card={card}
              pinned={el.static}
              deleteHandler={deleteHandler}
              onConfigItem={onConfigItem}
              onPinItem={pinHandler}
            />
          </CardWrapper>
        );
      });
    };
    return (
      <div className="id-antialiased id-font-sans id-flex-1 id-bg-gray-200 id-h-auto id-min-h-screen id-text-black id-flex-grow">
        <DashboardAppBar
          title={title}
          addClick={onAddItem}
          refreshClick={resetLayout}
          toggleDarkMode={toggleDarkMode}
          downloadHandler={() => onDashboardConfig(true)}
          saveHandler={() => onSaveConfig(false)}
        />
        <GridWrapper>
          <ResponsiveReactGridLayout
            className="layout"
            layouts={layouts}
            measureBeforeMount={false}
            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={rowHeight >= 100 ? rowHeight : 100}
            draggableCancel="input,textarea,.grid-layout"
            onBreakpointChange={(breakpoint: any, cols: any) =>
              onBreakpointChange(breakpoint, cols)
            }
            onLayoutChange={(layout: any, layouts: any) =>
              onLayoutChange(layout, layouts)
            }
            containerPadding={[12, 12]}
          >
            {renderGridCards()}
          </ResponsiveReactGridLayout>
        </GridWrapper>
      </div>
    );
  }
);
